import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import MembershipPlansEditor from '../gyms/MembershipPlansEditor';
import { uploadFileInChunks, abortUpload } from '../../utils/chunkedUpload';
import { parseVideoUrl } from '../../utils/videoUrl';

const SiteVisitModal = ({
  isOpen,
  onClose,
  onSuccess,
  gym,
  fromStage,
  toStage
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('visit_result'); // visit_result, details
  const [activeTab, setActiveTab] = useState('details'); // details, photos, members
  const [visitResult, setVisitResult] = useState(null); // owner_met, owner_not_met, photos_only
  const [visitNotes, setVisitNotes] = useState('');
  const [nextVisitDate, setNextVisitDate] = useState(''); // For scheduling next visit when owner not met
  const fileInputRef = useRef(null);
  const memberFileInputRef = useRef(null);

  // GPS Location state
  const [gpsLocation, setGpsLocation] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState('');

  // Online/offline state — drives a warning banner so users notice the connection
  // problem before they fill out the form and hit submit.
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onUp = () => setIsOnline(true);
    const onDown = () => setIsOnline(false);
    window.addEventListener('online', onUp);
    window.addEventListener('offline', onDown);
    return () => {
      window.removeEventListener('online', onUp);
      window.removeEventListener('offline', onDown);
    };
  }, []);

  const [formData, setFormData] = useState({
    // Basic Info
    name: '',
    address: '',
    city: '',
    state: '',
    pinCode: '',
    latitude: '',
    longitude: '',
    // Owner Info
    ownerName: '',
    ownerContact: '',
    ownerEmail: '',
    phoneNumber: '',
    // Gym Details
    size: '',
    proposalAmount: '',
    ctoAmount: '',
    rentAmount: '',
    closerPercentage: '',
    remarks: '',
    // Visit Info
    visitDateTime: '',
    footfall: '',
    activeMembersCount: '',
    // Location
    locationDescription: '',
    // New site visit fields
    membershipPlans: []
  });

  // Each photo entry: { id, file, preview, isVideo, uploadId, percent, status, error, controller, originalSize }
  // status: 'uploading' | 'done' | 'error' | 'aborted'
  const [photos, setPhotos] = useState([]);
  // Mirror of `photos` for reads from non-rendering callers (e.g. polling inside
  // handleSubmit). Avoids stale-closure reads of the state setter's previous value.
  const photosRef = useRef([]);
  useEffect(() => { photosRef.current = photos; }, [photos]);

  // Map<photoId, Promise<{uploadId}>>. Lets the background submit await uploads
  // even after the modal unmounts (state polling stops working post-unmount).
  const uploadPromisesRef = useRef(new Map());

  // Flipped when we kick off a fire-and-forget submit so handleClose knows NOT
  // to abort in-flight uploads on its way out.
  const backgroundSubmitRef = useRef(false);
  // External video URLs (YouTube/Vimeo/Google Drive/direct). Each: { id, raw, parsed }
  const [videoUrls, setVideoUrls] = useState([]);
  const [videoUrlInput, setVideoUrlInput] = useState('');
  const [videoUrlError, setVideoUrlError] = useState('');
  const [memberListFile, setMemberListFile] = useState(null);

  // Stable IDs for entries so async progress updates can find the right item.
  const nextPhotoId = useRef(0);
  const makePhotoId = () => `p${++nextPhotoId.current}`;

  // Backward-compat aliases used throughout the JSX below.
  const gymPhotos = photos;
  const photoPreview = photos;
  const setGymPhotos = setPhotos;
  const setPhotoPreview = setPhotos;

  // Get current datetime for default value
  const getCurrentDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  // Function to get GPS location
  const getGpsLocation = () => {
    if (!navigator.geolocation) {
      setGpsError('Geolocation not supported');
      return;
    }

    setGpsLoading(true);
    setGpsError('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        };
        setGpsLocation(location);
        // Also update form data with coordinates
        setFormData(prev => ({
          ...prev,
          latitude: position.coords.latitude.toString(),
          longitude: position.coords.longitude.toString()
        }));
        setGpsLoading(false);
      },
      (error) => {
        let errorMessage = 'Location error';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
        }
        setGpsError(errorMessage);
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Function to handle "Try Again" - checks permission status first
  const handleRetryLocation = async () => {
    setGpsLoading(true);
    setGpsError('');

    // Check if Permissions API is available
    if (navigator.permissions && navigator.permissions.query) {
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });

        if (permissionStatus.state === 'denied') {
          // Permission is still denied - show instructions
          setGpsError('Location access is blocked. Please enable location in your browser settings and try again.');
          setGpsLoading(false);
          toast.error('Location is blocked. Please enable it in browser settings.', {
            autoClose: 5000,
          });
          return;
        } else if (permissionStatus.state === 'granted') {
          // Permission granted - get location
          getGpsLocation();
        } else {
          // Permission is 'prompt' - will ask user again
          getGpsLocation();
        }
      } catch (err) {
        // Permissions API failed, try getting location directly
        getGpsLocation();
      }
    } else {
      // Permissions API not available, try getting location directly
      getGpsLocation();
    }
  };

  // Initialize form with existing gym data
  useEffect(() => {
    if (isOpen && gym) {
      setFormData({
        name: gym.name || '',
        address: gym.address || '',
        city: gym.location || '',
        state: gym.state || '',
        pinCode: gym.pinCode || '',
        latitude: gym.gps_lat?.toString() || '',
        longitude: gym.gps_lng?.toString() || '',
        ownerName: gym.contact_person || '',
        ownerContact: gym.ownerContact || '',
        ownerEmail: gym.ownerEmail || '',
        phoneNumber: gym.phone || '',
        size: gym.size?.toString() || '',
        proposalAmount: gym.proposalAmount?.toString() || '',
        ctoAmount: gym.ctoAmount?.toString() || '',
        rentAmount: gym.rentAmount?.toString() || '',
        closerPercentage: gym.closerPercentage?.toString() || '',
        remarks: '',
        visitDateTime: getCurrentDateTime(),
        footfall: '',
        activeMembersCount: gym.activeMembersCount?.toString() || '',
        locationDescription: gym.locationDescription || ''
      });
      setGymPhotos([]);
      setMemberListFile(null);
      setPhotoPreview([]);
      setVideoUrls([]);
      setVideoUrlInput('');
      setVideoUrlError('');
      setActiveTab('details');
      setStep('visit_result');
      setVisitResult(null);
      setVisitNotes('');
      setNextVisitDate('');
      setError(null);
      setGpsLocation(null);
      setGpsError('');
      // Automatically get GPS location when modal opens
      getGpsLocation();
    }
  }, [isOpen, gym]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const updatePhoto = (id, patch) => {
    setPhotos(prev => prev.map(p => (p.id === id ? { ...p, ...patch } : p)));
  };

  const startChunkedUpload = (entry) => {
    const controller = new AbortController();
    updatePhoto(entry.id, { controller, status: 'uploading' });

    const promise = uploadFileInChunks(entry.file, {
      signal: controller.signal,
      onProgress: ({ percent }) => updatePhoto(entry.id, { percent }),
    });

    uploadPromisesRef.current.set(entry.id, promise);

    // UI-side effects only; the awaitable promise above is what handleSubmit uses.
    promise.then((result) => {
      updatePhoto(entry.id, {
        uploadId: result.uploadId,
        percent: 100,
        status: 'done',
      });
    }).catch((err) => {
      if (err?.name === 'AbortError') {
        updatePhoto(entry.id, { status: 'aborted' });
        return;
      }
      console.error('Chunked upload failed:', err);
      updatePhoto(entry.id, {
        status: 'error',
        error: err?.response?.data?.error || err?.message || 'Upload failed',
      });
      toast.error(`Upload failed: ${entry.file.name}`);
    });

    return promise;
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + photos.length > 10) {
      toast.error('Maximum 10 files allowed');
      return;
    }

    const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1 GB per file
    const oversized = files.filter(f => f.size > MAX_FILE_SIZE);
    if (oversized.length > 0) {
      toast.error(`File too large: ${oversized[0].name} (${(oversized[0].size / 1024 / 1024).toFixed(1)} MB). Max 1 GB per file.`);
      return;
    }

    const validFiles = files.filter(f => f.type.startsWith('image/') || f.type.startsWith('video/'));
    if (validFiles.length !== files.length) {
      toast.error('Only image and video files are allowed');
    }

    // Build entries + previews, then start chunked uploads in parallel.
    validFiles.forEach(file => {
      const id = makePhotoId();
      const isVideo = file.type.startsWith('video/');
      const entry = {
        id,
        file,
        preview: null,
        isVideo,
        uploadId: null,
        percent: 0,
        originalSize: file.size,
        status: 'uploading',
        error: null,
        controller: null,
      };
      setPhotos(prev => [...prev, entry]);

      const reader = new FileReader();
      reader.onloadend = () => updatePhoto(id, { preview: reader.result });
      reader.readAsDataURL(file);

      startChunkedUpload(entry);
    });

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const retryUpload = (id) => {
    const entry = photos.find(p => p.id === id);
    if (!entry) return;
    updatePhoto(id, {
      status: 'uploading',
      percent: 0,
      error: null,
      uploadId: null,
    });
    startChunkedUpload(entry);
  };

  const removePhoto = (index) => {
    const entry = photos[index];
    if (entry) {
      try { entry.controller?.abort(); } catch {}
      if (entry.uploadId) abortUpload(entry.uploadId);
    }
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const addVideoUrl = () => {
    const raw = videoUrlInput.trim();
    if (!raw) {
      setVideoUrlError('Paste a video URL first');
      return;
    }
    const parsed = parseVideoUrl(raw);
    if (!parsed) {
      setVideoUrlError('Unsupported URL. Use a YouTube, Vimeo, Google Drive, or direct video link.');
      return;
    }
    if (videoUrls.some(v => v.parsed.url === parsed.url)) {
      setVideoUrlError('This video URL is already added');
      return;
    }
    if (photos.length + videoUrls.length >= 10) {
      setVideoUrlError('Maximum 10 media items (photos + videos + URLs)');
      return;
    }
    setVideoUrls(prev => [...prev, { id: `u${Date.now()}`, raw, parsed }]);
    setVideoUrlInput('');
    setVideoUrlError('');
  };

  const removeVideoUrl = (id) => {
    setVideoUrls(prev => prev.filter(v => v.id !== id));
  };

  const handleMemberFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['.xlsx', '.xls', '.csv'];
      const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (!validTypes.includes(extension)) {
        toast.error('Please upload Excel (.xlsx, .xls) or CSV file');
        return;
      }
      setMemberListFile(file);
    }
  };

  const removeMemberFile = () => {
    setMemberListFile(null);
    if (memberFileInputRef.current) {
      memberFileInputRef.current.value = '';
    }
  };

  const validateForm = () => {
    const errors = [];

    if (!visitResult) errors.push('Please select a visit result');
    if (!formData.visitDateTime) errors.push('Visit date & time is required');

    // Visit notes are always required
    if (!visitNotes?.trim()) {
      errors.push('Visit notes are required');
    } else if (visitNotes.trim().length < 10) {
      errors.push('Visit notes must be at least 10 characters');
    }

    // For owner_met, require more details
    if (visitResult === 'owner_met') {
      if (!formData.name?.trim()) errors.push('Gym name is required');
      if (!formData.city?.trim()) errors.push('City is required');
      // Photos / videos / URLs are optional — visit can be logged without media.
    }

    // Failed uploads still block — user must retry or remove them. In-progress
    // uploads no longer block: handleSubmit awaits them and finalizes in background.
    const failed = photos.filter(p => p.status === 'error').length;
    if (failed > 0) {
      errors.push(`${failed} file(s) failed to upload — please retry or remove them`);
    }

    if (errors.length > 0) {
      setError(errors.join(', '));
      return false;
    }
    return true;
  };

  // Builds the FormData payload for /api/gyms/site-visit. Pulled into a helper
  // so both the foreground submit and the background submit produce identical
  // payloads. `alreadyDoneUploadIds` are photos that already finished by the
  // time submit was clicked; `awaitedUploadIds` come from promises that resolved
  // after the modal closed.
  const buildSubmitData = (alreadyDoneUploadIds, awaitedUploadIds) => {
    const submitData = new FormData();
    submitData.append('gymId', gym.id);
    submitData.append('visitResult', visitResult);
    submitData.append('visitDateTime', formData.visitDateTime);
    submitData.append('visitNotes', visitNotes);
    submitData.append('name', formData.name);
    submitData.append('address', formData.address || '');
    submitData.append('city', formData.city);
    submitData.append('state', formData.state || '');
    submitData.append('pinCode', formData.pinCode || '');
    submitData.append('latitude', formData.latitude || '');
    submitData.append('longitude', formData.longitude || '');
    submitData.append('ownerName', formData.ownerName || '');
    submitData.append('ownerContact', formData.ownerContact || '');
    submitData.append('ownerEmail', formData.ownerEmail || '');
    submitData.append('phoneNumber', formData.phoneNumber || '');
    submitData.append('size', formData.size || '');
    submitData.append('proposalAmount', formData.proposalAmount || '');
    submitData.append('ctoAmount', formData.ctoAmount || '');
    submitData.append('rentAmount', formData.rentAmount || '');
    submitData.append('closerPercentage', formData.closerPercentage || '');
    submitData.append('remarks', formData.remarks || '');
    submitData.append('footfall', formData.footfall || '');
    submitData.append('activeMembersCount', formData.activeMembersCount || '');
    submitData.append('locationDescription', formData.locationDescription || '');
    const filteredPlans = (formData.membershipPlans || []).filter(p => p.name);
    if (filteredPlans.length > 0) submitData.append('membershipPlans', JSON.stringify(filteredPlans));
    if (visitResult === 'owner_not_met' && nextVisitDate) {
      submitData.append('nextVisitDate', nextVisitDate);
    }
    const allIds = Array.from(new Set([...alreadyDoneUploadIds, ...awaitedUploadIds]));
    submitData.append('uploadIds', JSON.stringify(allIds));
    submitData.append('videoUrls', JSON.stringify(videoUrls.map(v => v.parsed.url)));
    if (memberListFile) submitData.append('memberList', memberListFile);
    return submitData;
  };

  const postSiteVisit = async (submitData) => {
    return axios.post('/api/gyms/site-visit', submitData, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'multipart/form-data',
      },
    });
  };

  const formatSubmitError = (error) => {
    const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
    const noResponse = !error.response;
    const networkCode = error.code === 'ERR_NETWORK'
      || error.code === 'ECONNABORTED'
      || error.code === 'ETIMEDOUT';
    if (isOffline) {
      return 'You appear to be offline. Your file did not upload. Please check your internet connection and tap "Complete Site Visit" again.';
    }
    if (noResponse || networkCode) {
      return 'Network error — your file could not be uploaded. This is usually a slow or unstable connection. Please move to a better signal and tap "Complete Site Visit" again.';
    }
    if (error.response?.status === 413) {
      return 'File too large for the server to accept. Try a smaller video (under 200 MB) or upload one file at a time.';
    }
    if (error.response?.status >= 500) {
      return error.response?.data?.error || 'Server error. Please try again in a moment.';
    }
    return error.response?.data?.error || 'Failed to save site visit details. Please try again.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validateForm()) return;

    const alreadyDoneUploadIds = photosRef.current.map(p => p.uploadId).filter(Boolean);
    const pendingPromises = Array.from(uploadPromisesRef.current.entries())
      .filter(([id]) => {
        const entry = photosRef.current.find(p => p.id === id);
        return entry && entry.status === 'uploading';
      })
      .map(([, p]) => p);

    // Always close the modal immediately — uploads (if any) and the POST run
    // in the background. The detached task must not touch React state after
    // kickoff because the modal will unmount.
    const gymLabel = gym?.name || 'site visit';
    const hasPending = pendingPromises.length > 0;
    toast.info(
      hasPending
        ? `Submitting ${gymLabel} in background — uploads will finish on their own.`
        : `Submitting ${gymLabel} in background…`,
      { autoClose: 4000 }
    );
    backgroundSubmitRef.current = true;

    (async () => {
      try {
        let awaitedUploadIds = [];
        let failedCount = 0;
        if (hasPending) {
          const settled = await Promise.allSettled(pendingPromises);
          awaitedUploadIds = settled
            .filter(r => r.status === 'fulfilled' && r.value?.uploadId)
            .map(r => r.value.uploadId);
          failedCount = settled.filter(r => r.status === 'rejected').length;

          if (failedCount > 0 && (alreadyDoneUploadIds.length + awaitedUploadIds.length) === 0) {
            toast.error(`Background upload failed for ${gymLabel}. Please retry the site visit.`, { autoClose: 8000 });
            return;
          }
        }

        const response = await postSiteVisit(buildSubmitData(alreadyDoneUploadIds, awaitedUploadIds));
        if (failedCount > 0) {
          toast.warn(`${gymLabel} saved, but ${failedCount} file(s) failed to upload.`, { autoClose: 6000 });
        } else {
          toast.success(`${gymLabel} completed successfully!`);
        }
        onSuccess({ gym: response.data.gym });
      } catch (err) {
        console.error('Background site visit submit failed:', err);
        toast.error(`Background submit failed: ${formatSubmitError(err)}`, { autoClose: 8000 });
      }
    })();

    // Close the modal immediately — the user can move on with their work.
    handleClose();
  };

  const handleClose = () => {
    if (!submitting) {
      setFormData({
        name: '',
        address: '',
        city: '',
        state: '',
        pinCode: '',
        latitude: '',
        longitude: '',
        ownerName: '',
        ownerContact: '',
        ownerEmail: '',
        phoneNumber: '',
        size: '',
        proposalAmount: '',
        ctoAmount: '',
        rentAmount: '',
        closerPercentage: '',
        remarks: '',
        visitDateTime: '',
        footfall: '',
        activeMembersCount: '',
        locationDescription: '',
        membershipPlans: []
      });
      // If we kicked off a background submit, leave the in-flight uploads alone —
      // the detached async task is still awaiting them.
      if (!backgroundSubmitRef.current) {
        photos.forEach(p => {
          try { p.controller?.abort(); } catch {}
          if (p.uploadId && p.status === 'done') abortUpload(p.uploadId);
        });
      }
      setPhotos([]);
      setMemberListFile(null);
      setVideoUrls([]);
      setVideoUrlInput('');
      setVideoUrlError('');
      setStep('visit_result');
      setVisitResult(null);
      setVisitNotes('');
      setNextVisitDate('');
      setError(null);
      onClose();
    }
  };

  // Proceed to details step after selecting visit result
  const handleProceedToDetails = () => {
    if (!visitResult) {
      setError('Please select a visit result');
      return;
    }
    if (!visitNotes?.trim()) {
      setError('Visit notes are required');
      return;
    }
    if (visitNotes.trim().length < 10) {
      setError('Visit notes must be at least 10 characters');
      return;
    }
    setError(null);
    setActiveTab('details');
    setStep('details');
  };

  // Go back to visit result step
  const handleBackToVisitResult = () => {
    setStep('visit_result');
    setError(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {step === 'visit_result' ? 'Log Site Visit' : 'Complete Site Visit Details'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              <strong>{gym?.name}</strong> - {gym?.location || 'Location not set'}
            </p>
            {gym?.visitCount > 0 && (
              <p className="text-xs text-orange-600 mt-1">
                Visit #{(gym?.visitCount || 0) + 1} of 3
              </p>
            )}
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={submitting}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs - Only show when in details step and owner_met */}
        {step === 'details' && visitResult === 'owner_met' && (
          <div className="border-b border-gray-200 bg-white">
            <nav className="flex px-6" aria-label="Tabs">
              {[
                { id: 'details', label: 'Gym Details', icon: '🏋️' },
                { id: 'photos', label: 'Photos', icon: '📷', badge: gymPhotos.length },
                { id: 'members', label: 'Active Members', icon: '👥' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-3 px-4 text-sm font-medium border-b-2 ${
                    activeTab === tab.id
                      ? 'border-red-500 text-red-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.label}
                  {tab.required && <span className="text-red-500 ml-1">*</span>}
                  {tab.badge > 0 && (
                    <span className="ml-2 bg-green-100 text-green-600 px-2 py-0.5 rounded-full text-xs">
                      {tab.badge}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* Offline banner — shown whenever the browser reports no connection */}
        {!isOnline && (
          <div className="mx-6 mt-4 p-3 bg-yellow-50 border border-yellow-300 rounded-md flex items-start gap-2">
            <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="text-sm text-yellow-800">
              <p className="font-semibold">No internet connection</p>
              <p>Your photos and form won&apos;t be saved until you&apos;re back online. Move to a better signal and the banner will disappear.</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Visit Result Selection */}
          {step === 'visit_result' && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">What was the result of your visit?</h3>
                <p className="text-sm text-gray-500 mt-1">Select the outcome of your site visit to {gym?.name}</p>
              </div>

              {/* Visit Result Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Owner Met */}
                <button
                  type="button"
                  onClick={() => setVisitResult('owner_met')}
                  className={`p-6 rounded-lg border-2 text-left transition-all ${
                    visitResult === 'owner_met'
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300 hover:bg-green-50'
                  }`}
                >
                  <div className="text-3xl mb-3">✅</div>
                  <h4 className="font-semibold text-gray-900">Owner Met</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Successfully met the owner and collected gym details
                  </p>
                </button>

                {/* Owner Not Met */}
                <button
                  type="button"
                  onClick={() => setVisitResult('owner_not_met')}
                  className={`p-6 rounded-lg border-2 text-left transition-all ${
                    visitResult === 'owner_not_met'
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                  }`}
                >
                  <div className="text-3xl mb-3">❌</div>
                  <h4 className="font-semibold text-gray-900">Owner Not Met</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Visited but couldn't meet the owner
                  </p>
                </button>
              </div>

              {/* Visit Date Time */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Visit Date & Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  name="visitDateTime"
                  value={formData.visitDateTime}
                  onChange={handleInputChange}
                  className="w-full md:w-1/2 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                  required
                />
              </div>

              {/* Visit Notes - Required */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Visit Notes <span className="text-red-500">*</span>
                  <span className="text-xs text-gray-400 ml-2">(min 10 characters)</span>
                </label>
                <textarea
                  value={visitNotes}
                  onChange={(e) => setVisitNotes(e.target.value)}
                  placeholder={visitResult === 'owner_not_met'
                    ? "Please explain why you couldn't meet the owner..."
                    : "Add details about this visit - what was discussed, observations, etc."}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                  required
                />
                <p className={`text-xs mt-1 ${visitNotes.trim().length >= 10 ? 'text-green-600' : 'text-gray-400'}`}>
                  {visitNotes.trim().length}/10 characters minimum
                </p>
              </div>

              {/* Next Visit Schedule - Only for owner_not_met */}
              {visitResult === 'owner_not_met' && (gym?.visitCount || 0) < 2 && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-blue-800 mb-2">
                        Schedule Next Visit
                        <span className="text-xs font-normal text-blue-600 ml-2">(Recommended)</span>
                      </label>
                      <p className="text-xs text-blue-600 mb-3">
                        Since you couldn't meet the owner, schedule your next visit attempt.
                      </p>
                      <input
                        type="datetime-local"
                        value={nextVisitDate}
                        onChange={(e) => setNextVisitDate(e.target.value)}
                        min={new Date().toISOString().slice(0, 16)}
                        className="w-full md:w-2/3 border border-blue-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        placeholder="Select next visit date & time"
                      />
                      {nextVisitDate && (
                        <p className="text-xs text-green-600 mt-2 flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Next visit scheduled for {new Date(nextVisitDate).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Warning for max visits */}
              {(gym?.visitCount || 0) >= 2 && visitResult === 'owner_not_met' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Warning: Final Visit Attempt</h3>
                      <p className="text-sm text-red-700 mt-1">
                        This is the 3rd visit attempt. If owner is not met, this lead will be moved to expired clients.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Details Entry - Only for owner_met or photos_only */}
          {step === 'details' && (
            <form onSubmit={handleSubmit}>
              {/* Details Tab - Only for owner_met */}
              {activeTab === 'details' && visitResult === 'owner_met' && (
              <div className="space-y-6">
                {/* Visit Info */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="text-sm font-semibold text-blue-800 mb-3">Visit Information</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Visit Date & Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      name="visitDateTime"
                      value={formData.visitDateTime}
                      onChange={handleInputChange}
                      className="w-full md:w-1/2 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                {/* Basic Info */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">Basic Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Gym Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter gym name"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Gym Phone Number
                        <span className="text-xs text-gray-400 ml-1">(10 digits)</span>
                      </label>
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                          setFormData(prev => ({ ...prev, phoneNumber: value }));
                        }}
                        inputMode="numeric"
                        maxLength={10}
                        placeholder="9876543210"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">Address Details</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Full Address
                      </label>
                      <textarea
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Enter complete address"
                        rows={2}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          City <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          placeholder="Enter city"
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          State
                        </label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          placeholder="Enter state"
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Pin Code
                        </label>
                        <input
                          type="text"
                          name="pinCode"
                          value={formData.pinCode}
                          onChange={handleInputChange}
                          placeholder="000000"
                          maxLength={6}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Location Description
                        </label>
                        <input
                          type="text"
                          name="locationDescription"
                          value={formData.locationDescription}
                          onChange={handleInputChange}
                          placeholder="Near landmark..."
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Owner Info */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">Owner Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Owner Name
                      </label>
                      <input
                        type="text"
                        name="ownerName"
                        value={formData.ownerName}
                        onChange={handleInputChange}
                        placeholder="Enter owner name"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Owner Contact
                        <span className="text-xs text-gray-400 ml-1">(10 digits)</span>
                      </label>
                      <input
                        type="tel"
                        name="ownerContact"
                        value={formData.ownerContact}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                          setFormData(prev => ({ ...prev, ownerContact: value }));
                        }}
                        inputMode="numeric"
                        maxLength={10}
                        placeholder="9876543210"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Owner Email
                      </label>
                      <input
                        type="email"
                        name="ownerEmail"
                        value={formData.ownerEmail}
                        onChange={handleInputChange}
                        placeholder="owner@example.com"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Business Info */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">Business Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Size (Square Feet)
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        name="size"
                        value={formData.size}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          if (value === '' || parseInt(value) > 0) {
                            setFormData(prev => ({ ...prev, size: value }));
                          }
                        }}
                        placeholder="e.g., 5000"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Expected Proposal Amount (Rs)
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        name="proposalAmount"
                        value={formData.proposalAmount}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          if (value === '' || parseInt(value) >= 0) {
                            setFormData(prev => ({ ...prev, proposalAmount: value }));
                          }
                        }}
                        placeholder="e.g., 1000000"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        CTO Amount (Rs)
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        name="ctoAmount"
                        value={formData.ctoAmount}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          if (value === '' || parseInt(value) >= 0) {
                            setFormData(prev => ({ ...prev, ctoAmount: value }));
                          }
                        }}
                        placeholder="e.g., 500000"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Rent Amount (Rs/Month)
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        name="rentAmount"
                        value={formData.rentAmount}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          if (value === '' || parseInt(value) >= 0) {
                            setFormData(prev => ({ ...prev, rentAmount: value }));
                          }
                        }}
                        placeholder="e.g., 50000"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Closer Percentage (%)
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        name="closerPercentage"
                        value={formData.closerPercentage}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          const numValue = parseFloat(value);
                          if (value === '' || (numValue >= 0 && numValue <= 100)) {
                            setFormData(prev => ({ ...prev, closerPercentage: value }));
                          }
                        }}
                        placeholder="e.g., 10"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Remarks / Notes
                    </label>
                    <textarea
                      name="remarks"
                      value={formData.remarks}
                      onChange={handleInputChange}
                      placeholder="Add any additional notes about the gym..."
                      rows={3}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                </div>

                {/* Membership Plans */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">Membership Plans</h3>
                  <MembershipPlansEditor
                    plans={formData.membershipPlans || []}
                    onChange={plans => setFormData(prev => ({ ...prev, membershipPlans: plans }))}
                  />
                </div>

              </div>
            )}

            {/* Photos Tab */}
            {activeTab === 'photos' && (
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Optional:</strong> Upload photos or videos of the gym, or paste a YouTube / Vimeo / Google Drive link below. You can add up to 10 items.
                  </p>
                </div>

                {/* Upload Area - Photos and Videos from device */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-red-300 rounded-lg p-8 text-center cursor-pointer hover:border-red-500 hover:bg-red-50 transition-colors bg-red-25"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                    onClick={(e) => {
                      e.target.value = '';
                    }}
                  />
                  <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="h-10 w-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p className="text-lg font-semibold text-red-700">
                    Tap to Upload Photos or Videos
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Select files from your device (images or videos, up to 1 GB each)
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Uploads continue in the background — you can submit the visit while videos finish uploading.
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    {gymPhotos.length}/10 files uploaded
                  </p>
                </div>

                {/* Or paste a video URL */}
                <div className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23 7.1c-.3-1.1-1.1-1.9-2.2-2.2C18.8 4.5 12 4.5 12 4.5s-6.8 0-8.8.4c-1.1.3-1.9 1.1-2.2 2.2C.6 9.1.6 12 .6 12s0 2.9.4 4.9c.3 1.1 1.1 1.9 2.2 2.2 2 .4 8.8.4 8.8.4s6.8 0 8.8-.4c1.1-.3 1.9-1.1 2.2-2.2.4-2 .4-4.9.4-4.9s0-2.9-.4-4.9zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z"/>
                    </svg>
                    <h4 className="text-sm font-medium text-gray-800">
                      Or paste a YouTube / Vimeo / Google Drive link
                    </h4>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    Useful if the owner already has the gym video online — no upload needed.
                    <span className="block mt-1 text-amber-700">
                      For Google Drive: make sure the file is shared as <strong>"Anyone with the link → Viewer"</strong>, otherwise others will see a login wall.
                    </span>
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="url"
                      value={videoUrlInput}
                      onChange={(e) => { setVideoUrlInput(e.target.value); setVideoUrlError(''); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addVideoUrl(); } }}
                      placeholder="https://youtube.com/... or https://drive.google.com/file/d/..."
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                    <button
                      type="button"
                      onClick={addVideoUrl}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                    >
                      Add URL
                    </button>
                  </div>
                  {videoUrlError && (
                    <p className="text-xs text-red-600 mt-2">{videoUrlError}</p>
                  )}

                  {videoUrls.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {videoUrls.map(v => (
                        <div key={v.id} className="flex items-center gap-3 bg-gray-50 rounded-md p-2 border border-gray-200">
                          {v.parsed.thumbnailUrl ? (
                            <img
                              src={v.parsed.thumbnailUrl}
                              alt="video thumbnail"
                              className="w-24 h-14 object-cover rounded border border-gray-300 bg-black flex-shrink-0"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-24 h-14 rounded border border-gray-300 bg-gray-200 flex items-center justify-center text-gray-500 text-xs flex-shrink-0">
                              {v.parsed.provider}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-800 truncate">
                              {v.parsed.provider === 'youtube' ? 'YouTube'
                                : v.parsed.provider === 'vimeo' ? 'Vimeo'
                                : v.parsed.provider === 'drive' ? 'Google Drive'
                                : 'Video link'}
                            </p>
                            <a
                              href={v.parsed.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline truncate block"
                            >
                              {v.parsed.url}
                            </a>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeVideoUrl(v.id)}
                            className="text-red-500 hover:text-red-700 flex-shrink-0"
                            title="Remove"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Media Previews */}
                {photoPreview.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Uploaded Files:</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {photoPreview.map((item, index) => {
                        const borderClass =
                          item.status === 'done' ? 'border-green-300'
                          : item.status === 'error' ? 'border-red-400'
                          : item.status === 'aborted' ? 'border-gray-300'
                          : 'border-yellow-400';
                        return (
                          <div key={item.id} className="relative group">
                            {item.preview ? (
                              item.isVideo ? (
                                <video
                                  src={item.preview}
                                  controls
                                  className={`w-full h-32 object-cover rounded-lg border-2 ${borderClass} bg-black`}
                                />
                              ) : (
                                <img
                                  src={item.preview}
                                  alt={`Gym media ${index + 1}`}
                                  className={`w-full h-32 object-cover rounded-lg border-2 ${borderClass}`}
                                />
                              )
                            ) : (
                              <div className={`w-full h-32 rounded-lg border-2 ${borderClass} bg-gray-100 flex items-center justify-center text-gray-400 text-xs`}>
                                Loading preview…
                              </div>
                            )}

                            <button
                              type="button"
                              onClick={() => removePhoto(index)}
                              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 shadow-lg hover:bg-red-600"
                              title="Remove"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>

                            {item.status === 'uploading' && (
                              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex flex-col items-center justify-center text-white">
                                <div className="text-xs font-medium mb-1">
                                  Uploading… {Math.round(item.percent || 0)}%
                                </div>
                                <div className="w-3/4 h-1.5 bg-white/30 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-white transition-all"
                                    style={{ width: `${item.percent || 0}%` }}
                                  />
                                </div>
                              </div>
                            )}

                            {item.status === 'error' && (
                              <div className="absolute inset-0 bg-red-600/70 rounded-lg flex flex-col items-center justify-center text-white p-2 text-center">
                                <div className="text-xs font-medium mb-2">Upload failed</div>
                                <button
                                  type="button"
                                  onClick={(e) => { e.preventDefault(); retryUpload(item.id); }}
                                  className="text-xs bg-white text-red-600 px-2 py-1 rounded font-medium"
                                >
                                  Retry
                                </button>
                              </div>
                            )}

                            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs py-1 text-center rounded-b-lg">
                              {item.isVideo ? 'Video' : 'Photo'} {index + 1}
                              {item.status === 'done' && ' ✓'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}


                {photos.length > 0 && photos.length < 10 && (() => {
                  const doneCount = photos.filter(p => p.status === 'done').length;
                  const uploadingCount = photos.filter(p => p.status === 'uploading').length;
                  const errorCount = photos.filter(p => p.status === 'error').length;
                  if (uploadingCount > 0) {
                    return (
                      <div className="text-center p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                        <p className="text-yellow-800 font-medium">
                          Uploading {uploadingCount} file(s)…
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {doneCount} done, {uploadingCount} in progress
                        </p>
                      </div>
                    );
                  }
                  if (errorCount > 0) {
                    return (
                      <div className="text-center p-3 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-red-700 font-medium">
                          {errorCount} file(s) failed — tap Retry on the tile
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-green-700 font-medium">
                        {doneCount} file(s) uploaded successfully
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        Tap above to upload more (max 10)
                      </p>
                    </div>
                  );
                })()}

                {gymPhotos.length >= 10 && (
                  <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-blue-700 font-medium">
                      Maximum files reached (10/10)
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Members Tab */}
            {activeTab === 'members' && (
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-green-800">
                    <strong>Optional:</strong> Enter the total number of active members and/or upload a list of active gym members. This helps in understanding the gym's current membership base.
                  </p>
                </div>

                {/* Total Active Members Count */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Active Members
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="activeMembersCount"
                    value={formData.activeMembersCount}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      setFormData(prev => ({ ...prev, activeMembersCount: value }));
                    }}
                    placeholder="e.g., 250"
                    className="w-full md:w-1/2 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Approximate count of currently active members at the gym
                  </p>
                </div>

                {/* Upload Area */}
                <div
                  onClick={() => memberFileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-green-500 transition-colors"
                >
                  <input
                    ref={memberFileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleMemberFileUpload}
                    className="hidden"
                  />
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-600">
                    Click to upload member list
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Excel (.xlsx, .xls) or CSV files
                  </p>
                </div>

                {/* Uploaded File */}
                {memberListFile && (
                  <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-8 h-8 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{memberListFile.name}</p>
                        <p className="text-xs text-gray-500">
                          {(memberListFile.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removeMemberFile}
                      className="text-red-500 hover:text-red-700"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                )}

                {/* Template Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-800 mb-2">Expected File Format:</h4>
                  <p className="text-xs text-gray-600">
                    The file should contain columns like: Name, Phone, Email, Membership Type, Join Date, Expiry Date
                  </p>
                </div>
              </div>
            )}
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          {/* Left side - status info */}
          <div className="text-sm text-gray-500">
            {step === 'visit_result' ? (
              visitResult ? (
                <span className="text-green-600">
                  Selected: {visitResult === 'owner_met' ? 'Owner Met' : 'Owner Not Met'}
                </span>
              ) : (
                <span className="text-gray-400">Select visit result to continue</span>
              )
            ) : (
              <>
                {gymPhotos.length > 0 ? (
                  <span className="text-green-600">{gymPhotos.length} file(s) uploaded</span>
                ) : (
                  <span className="text-red-500">No files uploaded</span>
                )}
                {memberListFile && (
                  <span className="ml-3 text-green-600">Member list added</span>
                )}
              </>
            )}
          </div>

          {/* Right side - buttons */}
          <div className="flex space-x-3">
            {step === 'details' && (
              <button
                type="button"
                onClick={handleBackToVisitResult}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={submitting}
              >
                Back
              </button>
            )}
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={submitting}
            >
              Cancel
            </button>

            {step === 'visit_result' ? (
              /* Buttons for visit result step */
              visitResult === 'owner_not_met' ? (
                /* For owner not met - submit directly */
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting || !visitResult || visitNotes.trim().length < 10}
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Logging Visit...
                    </>
                  ) : (
                    'Log Visit (Owner Not Met)'
                  )}
                </button>
              ) : (
                /* For owner met or photos only - proceed to details */
                <button
                  type="button"
                  onClick={handleProceedToDetails}
                  disabled={!visitResult || visitNotes.trim().length < 10}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue to Details
                </button>
              )
            ) : (
              /* Submit button for details step */
              <button
                type="button"
                onClick={handleSubmit}
                disabled={
                  submitting
                  || photos.some(p => p.status === 'error')
                }
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving…
                  </>
                ) : (
                  'Complete Site Visit'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SiteVisitModal;
